<template>
  <div class="experience-card" :class="{ 'experience-card--multiple': isFromMultiple }">
    <div class="experience-card-date-range">{{ displayRangeDate }}</div>
    <div class="experience-card-content">
      <div v-if="isOtherXp" class="experience-card-qualification-item">{{ shift.qualification_name }}</div>
      <div v-if="displayDetail" class="experience-card-position-details">
        <div class="flex row">
          <div :class="shift.is_internal_xp ? `experience-card-company-internal` : `experience-card-company`">{{ displayName }}</div>
          <span v-if="shift.is_internal_xp" class="display-inline">
            <img :src="extracadabraPicture" alt="Extracadabra-logo" class="experience-card-logo q-ml-sm" />
          </span>
        </div>
      </div>
      <div class="flex row">
        <div class="experience-card-contract-badge">
          {{ shift.contract }}
        </div>
        <div class="experience-card-rating q-ml-sm">
          <div v-if="shift.rating" class="experience-card-stars">
            <span v-for="i in 5" :key="i" class="experience-card-star" :class="{ 'experience-card-star--filled': i <= shift.rating }">★</span>
          </div>
        </div>
      </div>
      <div v-if="shift.rating_comment" class="experience-card-comment">
        {{ shift.rating_comment }}
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref } from "vue";
  import { DatesHelpers } from "@core/helpers";
  import { limitText, PERSONALIZED_DATES_FORMATS } from "@extracadabra/core-v2";
  import { Screen } from "quasar";
  import { useI18n } from "vue-i18n";

  const picture = require("@etab/assets/img/extracadabra-logo.png");
  const format = PERSONALIZED_DATES_FORMATS.CANDIDATE_EXPERIENCES_DATE_FORMAT;

  const props = defineProps({
    shift: {
      type: Object,
      default: () => ({}),
    },
    displayDetail: {
      type: Boolean,
      default: true,
    },
    isFromMultiple: {
      type: Boolean,
      default: false,
    },
    isOtherXp: {
      type: Boolean,
      default: false,
    },
  });
  const { t } = useI18n();
  const extracadabraPicture = ref(picture);

  const limitByDevice = computed(() => (Screen.lt.sm ? 20 : 100));
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return DatesHelpers.getDayjs(dateString).format(format);
  };
  const displayNameFormatted = (etabName, etabType, limit) => {
    return `${limitText({ str: etabName, limit })} - ${etabType || ""}`;
  };
  const isSameMonthAndSameYear = (startDate, endDate) => {
    const start = DatesHelpers.getDayjs(startDate);
    const end = DatesHelpers.getDayjs(endDate);
    return start.month() === end.month() && start.year() === end.year();
  };
  const displayRangeDate = computed(() => {
    if (props.shift.end_date) {
      if (isSameMonthAndSameYear(props.shift.start_date, props.shift.end_date)) {
        return String(formatDate(props.shift.start_date)).charAt(0).toUpperCase() + String(formatDate(props.shift.start_date)).slice(1);
      } else {
        return t("ETAB.PAGES.EXTRA.@DATE_RANGE", { start: formatDate(props.shift.start_date), end: formatDate(props.shift.end_date) });
      }
    } else {
      return t("ETAB.PAGES.EXTRA.@DATE_RANGE_WITHOUT_END_DATE", {
        start: formatDate(props.shift.start_date),
      });
    }
  });
  const displayName = computed(() => {
    if (!props.shift.etab_name) return "";
    return displayNameFormatted(props.shift.etab_name, props.shift.etab_category, limitByDevice.value);
  });
</script>

<style lang="scss" scoped>
  .experience-card {
    padding: 0 10px 10px 10px;
    margin-bottom: 16px;
    border-radius: 8px;

    &--multiple {
      margin: 10px;
      box-shadow: $card-shadow;
    }

    &-date-range {
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    }
    &-qualification-item {
      font-weight: 600;
      font-size: 17px;
    }
    &-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    &-logo {
      width: 50px;
    }

    &-position-details {
      display: flex;
      flex-direction: column;
    }

    &-qualification {
      font-weight: bold;
      font-size: 14px;
    }

    &-company {
      font-size: 16px;
    }

    &-company-internal {
      font-size: 16px;
      max-width: 360px;
      @media screen and (max-width: $breakpoint-sm-max) {
        max-width: 180px;
      }
    }

    &-contract-badge {
      display: inline-block;
      padding: 5px 10px 3px 10px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
      color: white;
      background-color: #072c55;
    }

    &-rating {
      margin-top: 4px;
    }

    &-stars {
      display: flex;
    }

    &-star {
      color: #ddd;
      font-size: 16px;

      &--filled {
        color: #f8ce0b;
      }
    }

    &-comment {
      font-size: 16px;
      font-style: italic;
      color: #666;
      line-height: 1.4;
    }
  }
</style>
